
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TraceFlags flags
 */
class TraceFlags {
  /**
   * Initialize flags with a given value
   * @param {TraceFlags|number|Int64|UInt64=} value Flags value, defaults is 0
   * @constructor
   */
  constructor (value = 0) {
    if (value instanceof TraceFlags) {
      this.value = value.value
    } else {
      this.value = value
    }
  }

  /**
   * Check for the given flags set state
   * @this {!TraceFlags}
   * @param {!TraceFlags|!number|!Int64|!UInt64} flags Flags
   * @returns {!boolean} Flags set state
   */
  hasFlags (flags) {
    if (flags instanceof TraceFlags) {
      flags = flags.value
    }
    return ((this.value & flags) !== 0) && ((this.value & flags) === flags)
  }

  /**
   * Set the given flags
   * @this {!TraceFlags}
   * @param {!TraceFlags|!number|!Int64|!UInt64} flags Flags
   */
  setFlags (flags) {
    if (flags instanceof TraceFlags) {
      flags = flags.value
    }
    this.value |= flags
    return this
  }

  /**
   * Remove the given flags
   * @this {!TraceFlags}
   * @param {!TraceFlags|!number|!Int64|!UInt64} flags Flags
   */
  removeFlags (flags) {
    if (flags instanceof TraceFlags) {
      flags = flags.value
    }
    this.value &= ~flags
    return this
  }

  /**
   * Is this flags equal to other one?
   * @this {!TraceFlags}
   * @param {!TraceFlags} other Other flags
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TraceFlags)) {
      throw new TypeError('Instance of TraceFlags is required!')
    }
    return this.value === other.value
  }

  /**
   * Get flags value
   * @this {!TraceFlags}
   * @returns {!number|!Int64|!UInt64} Flags value
   */
  valueOf () {
    return this.value
  }

  /**
   * Convert flags to string
   * @this {!TraceFlags}
   * @returns {!string} Flags value string
   */
  toString () {
    let result = ''
    let first = true
    if ((this.value & TraceFlags.Sampled.value) && ((this.value & TraceFlags.Sampled.value) === TraceFlags.Sampled.value)) {
      result += (first ? '' : '|') + 'Sampled'
      // noinspection JSUnusedAssignment
      first = false
    }
    return result
  }

  /**
   * Inspect flags
   * @this {!TraceFlags}
   * @returns {!string} Flags value string
   */
  [util.inspect.custom] () {
    return this.toString()
  }

  /**
   * Convert flags to JSON
   * @this {!TraceFlags}
   * @returns {!number} Flags value for JSON
   */
  toJSON () {
    return this.value
  }

  /**
   * Create flags from number flags representation
   * @param {!number} flags Number flags representation
   * @returns {!TraceFlags} Created flags
   */
  static fromFlags (flags) {
    return new TraceFlags(flags)
  }

  /**
   * Create flags from object value
   * @param {!number} other Object value
   * @returns {!TraceFlags} Created flags
   */
  static fromObject (other) {
    return new TraceFlags(other)
  }
}

// noinspection PointlessArithmeticExpressionJS
TraceFlags.Sampled = new TraceFlags(0x01)

export { TraceFlags };

/**
 * Fast Binary Encoding TraceFlags field model
 */
class FieldModelTraceFlags extends fbe.FieldModel {
  /**
   * Get the field size
   * @this {!FieldModelTraceFlags}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 1
  }

  /**
   * Get the value
   * @this {!FieldModelTraceFlags}
   * @param {TraceFlags=} defaults Default value, defaults is new TraceFlags()
   * @returns {!TraceFlags} Result value
   */
  get (defaults = new TraceFlags()) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return defaults
    }

    return new TraceFlags(this.readByte(this.fbeOffset))
  }

  /**
   * Set the value
   * @this {!FieldModelTraceFlags}
   * @param {!TraceFlags} value Value
   */
  set (value) {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return
    }

    this.writeByte(this.fbeOffset, value.value)
  }
}

export { FieldModelTraceFlags };
